<template>
  <div class="p-4 min-h-screen bg-gray-100">
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 max-w-4xl mx-auto">
      <div class="mb-4">
        <h1 class="text-xl font-semibold">Manage Mailbox</h1>
        <p class="text-sm text-gray-500 mt-1">
          Your outreach mailbox: the signature and logos that go out on emails sent from it, and who else
          is allowed to send from it. Everyone can always send from their own email.
        </p>
      </div>

      <div v-if="loading" class="text-gray-500 py-8 text-center">Loading…</div>

      <template v-else>
        <!-- Mailboxes delegated TO this user: they can send from these, but the
             owner controls the signature, so they're listed read-only. -->
        <div v-if="grantedToMe.length" class="mb-6">
          <h2 class="text-sm font-semibold text-gray-700">Mailboxes you can send from</h2>
          <p class="text-xs text-gray-500 mt-0.5 mb-2">
            Granted to you by their owner. The owner sets the signature for these.
          </p>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="a in grantedToMe"
              :key="a.id"
              class="bg-gray-100 border border-gray-300 rounded-md px-2 py-1 text-sm"
            >{{ a.email_address }}</span>
          </div>
        </div>

        <h2 class="text-sm font-semibold text-gray-700 mb-2">Your mailboxes</h2>
        <div v-if="mailboxes.length === 0" class="text-gray-500 py-6 text-center">
          You don't manage any outreach mailboxes.
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="mailbox in mailboxes"
            :key="mailbox.id"
            class="border border-gray-200 rounded-md"
          >
            <!-- Collapsed row -->
            <button
              type="button"
              @click="toggle(mailbox.id)"
              class="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
            >
              <ChevronDownIcon v-if="isExpanded(mailbox.id)" class="w-4 h-4 text-gray-400 shrink-0" />
              <ChevronRightIcon v-else class="w-4 h-4 text-gray-400 shrink-0" />
              <span class="font-medium text-gray-800 shrink-0">{{ mailbox.email_address }}</span>
              <span
                v-if="mailbox.is_owner"
                class="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0"
              >yours</span>
              <span class="text-sm text-gray-500 truncate ml-auto pl-3">{{ summary(mailbox) }}</span>
            </button>

            <div v-if="isExpanded(mailbox.id)" class="border-t border-gray-100">
              <!-- Signature & logos -->
              <div class="px-3 py-3">
                <div class="flex items-baseline justify-between">
                  <h3 class="text-sm font-semibold text-gray-700">Signature &amp; logos</h3>
                  <span class="text-xs text-gray-400">used when the email template doesn't supply its own</span>
                </div>

                <div v-if="!mailbox.can_manage" class="text-sm text-gray-400 mt-2">
                  Only this mailbox's owner can change its signature.
                </div>

                <template v-else>
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                    <div v-for="slot in IMAGE_SLOTS" :key="slot.field" class="flex flex-col gap-1">
                      <label class="text-xs font-medium text-gray-600">{{ slot.label }}</label>

                      <div
                        class="h-24 border border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden"
                      >
                        <img
                          v-if="previewUrl(mailbox, slot.field)"
                          :src="previewUrl(mailbox, slot.field)"
                          class="max-h-full max-w-full object-contain"
                          :alt="slot.label"
                        />
                        <span v-else class="text-xs text-gray-400">None</span>
                      </div>

                      <div class="flex items-center gap-2">
                        <label
                          class="text-xs px-2 py-1 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                        >
                          Choose…
                          <input
                            type="file"
                            accept="image/*"
                            class="hidden"
                            @change="chooseImage($event, mailbox.id, slot.field)"
                          />
                        </label>
                        <button
                          v-if="previewUrl(mailbox, slot.field)"
                          type="button"
                          class="text-xs text-gray-400 hover:text-red-600"
                          @click="clearImage(mailbox.id, slot.field)"
                        >Remove</button>
                        <button
                          v-if="mailbox[slot.field]"
                          type="button"
                          class="text-xs text-indigo-600 hover:underline"
                          title="Place this image in the signature below"
                          @click="insertIntoSignature(mailbox, slot)"
                        >Insert</button>
                      </div>
                    </div>
                  </div>

                  <div class="mt-4">
                    <label class="block text-xs font-medium text-gray-600 mb-1">Signature</label>
                    <p class="text-xs text-gray-400 mb-1">
                      Appended to emails sent from this mailbox. The Signature image above is
                      added below whatever you write here — or use <span class="font-medium">Insert</span>
                      on any image to place it exactly where you want.
                    </p>
                    <RichTextEditor
                      :ref="el => setEditorRef(mailbox.id, el)"
                      :model-value="signatureHtml(mailbox)"
                      @update:model-value="html => setSignatureHtml(mailbox.id, html)"
                    />
                  </div>

                  <div class="flex items-center gap-3 mt-3">
                    <button
                      type="button"
                      :disabled="!isDirty(mailbox.id) || saving[mailbox.id]"
                      @click="saveBranding(mailbox)"
                      class="px-3 py-1 bg-dark-primary text-white rounded-md text-sm disabled:opacity-40"
                    >{{ saving[mailbox.id] ? 'Saving…' : 'Save' }}</button>
                    <button
                      v-if="isDirty(mailbox.id)"
                      type="button"
                      @click="discardChanges(mailbox.id)"
                      class="text-sm text-gray-500 hover:text-gray-700"
                    >Discard</button>
                    <span v-if="isDirty(mailbox.id)" class="text-xs text-amber-600">Unsaved changes</span>
                  </div>
                </template>
              </div>

              <!-- Sender access -->
              <div class="px-3 py-3 border-t border-gray-100">
                <h3 class="text-sm font-semibold text-gray-700">Sender access</h3>
                <div class="text-xs text-gray-500 mt-0.5 mb-2">
                  Users allowed to send outreach from this mailbox. They see its queue; they can't change its signature.
                </div>

                <div v-if="mailbox.authorized_users.length" class="flex flex-wrap gap-2 mb-3">
                  <span
                    v-for="au in mailbox.authorized_users"
                    :key="au.user_id"
                    class="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  >
                    {{ au.name }}
                    <button
                      v-if="mailbox.can_manage"
                      type="button"
                      @click="revoke(mailbox, au.user_id)"
                      class="text-gray-400 hover:text-red-600 font-semibold"
                      title="Revoke access"
                    >×</button>
                  </span>
                </div>
                <div v-else class="text-sm text-gray-400 mb-3">No delegated users.</div>

                <div v-if="mailbox.can_manage" class="flex items-center gap-2">
                  <select v-model="selectedUser[mailbox.id]" class="border rounded-md p-1 text-sm max-w-xs">
                    <option :value="null">Select a user to grant…</option>
                    <option v-for="u in grantableUsers(mailbox)" :key="u.id" :value="u.id">{{ u.label }}</option>
                  </select>
                  <button
                    type="button"
                    :disabled="!selectedUser[mailbox.id]"
                    @click="grant(mailbox)"
                    class="px-3 py-1 bg-dark-primary text-white rounded-md text-sm disabled:opacity-40"
                  >Grant</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import RichTextEditor from '@/components/base-components/RichTextEditor.vue'
import { getMailboxes, grantSender, revokeSender, updateMailboxBranding } from '@/services/Mailbox'

// `cid` is how the stored markup addresses each image; `field` is where its
// url lives on the mailbox payload.
const IMAGE_SLOTS = [
  { field: 'signature_image', cid: 'signature', label: 'Signature' },
  { field: 'logo_image', cid: 'logo', label: 'Logo' },
  { field: 'logo_image_2', cid: 'logo_2', label: 'Second logo' },
]

const store = useStore()
const $toast = useToast()

const loading = ref(false)
const mailboxes = ref([])
const users = ref([])
const grantedToMe = ref([])
const selectedUser = reactive({})
const expanded = reactive({})
const saving = reactive({})
// Unsaved image edits, per mailbox: { [mailboxId]: { signature_image: File | '' } }.
// A File is an upload, '' is a removal, and a field that isn't here is untouched
// -- which is exactly what the endpoint expects, so this map is sent as-is.
const pending = reactive({})
// One RichTextEditor per mailbox, so the Insert buttons can reach theirs.
const editorRefs = reactive({})
// Object URLs created for local previews, so they can be revoked on unmount
// rather than leaking for the life of the page.
const objectUrls = []

function token() {
  return store.state.auth.token
}

function toggle(id) {
  expanded[id] = !expanded[id]
}

function isExpanded(id) {
  return !!expanded[id]
}

function summary(mailbox) {
  const names = mailbox.authorized_users.map(a => a.name)
  const access = names.length ? names.join(', ') : 'No delegated users'
  const images = IMAGE_SLOTS.filter(s => mailbox[s.field]).length
  const bits = []
  if (images) bits.push(`${images} image${images > 1 ? 's' : ''}`)
  if (mailbox.signature_html) bits.push('signature')
  return bits.length ? `${access} · ${bits.join(', ')}` : access
}

function grantableUsers(mailbox) {
  const granted = new Set(mailbox.authorized_users.map(a => a.user_id))
  return users.value.filter(u => !granted.has(u.id)).map(u => ({ id: u.id, label: u.name }))
}

// --- unsaved image edits -----------------------------------------------

function isDirty(id) {
  return Object.keys(pending[id] || {}).length > 0
}

function chooseImage(event, id, field) {
  const file = event.target.files && event.target.files[0]
  // Reset the input so re-picking the same file after a discard still fires.
  event.target.value = ''
  if (!file) return
  pending[id] = { ...(pending[id] || {}), [field]: file }
}

function clearImage(id, field) {
  pending[id] = { ...(pending[id] || {}), [field]: '' }
}

// Stored signature markup addresses images by cid: -- the only form that works
// in an email, where the image travels with the message. A browser can't resolve
// cid:, so the editor is fed real urls and the swap is undone on save. Stored
// form stays canonical; the editor just never sees it.
function cidToUrl(html, mailbox) {
  let out = html || ''
  for (const slot of IMAGE_SLOTS) {
    const url = mailbox[slot.field]
    if (url) out = out.split(`cid:${slot.cid}"`).join(`${url}"`)
  }
  return out
}

function urlToCid(html, mailbox) {
  let out = html || ''
  for (const slot of IMAGE_SLOTS) {
    const url = mailbox[slot.field]
    if (url) out = out.split(url).join(`cid:${slot.cid}`)
  }
  return out
}

// The signature markup, in editor (url) form.
function signatureHtml(mailbox) {
  const change = (pending[mailbox.id] || {}).signature_html
  return change !== undefined ? change : cidToUrl(mailbox.signature_html, mailbox)
}

function setEditorRef(id, el) {
  if (el) editorRefs[id] = el
  else delete editorRefs[id]
}

function insertIntoSignature(mailbox, slot) {
  // Insert the real url so the editor shows the actual image; it becomes a cid:
  // reference again on save.
  editorRefs[mailbox.id]?.insertImage(mailbox[slot.field])
}

function setSignatureHtml(id, html) {
  const mailbox = mailboxes.value.find(m => m.id === id)
  // The editor emits on its initial content set too, so ignore a "change" that
  // matches what's already stored -- otherwise opening a mailbox marks it dirty.
  if (mailbox && html === (mailbox.signature_html || '')) {
    const rest = { ...(pending[id] || {}) }
    delete rest.signature_html
    if (Object.keys(rest).length) pending[id] = rest
    else delete pending[id]
    return
  }
  pending[id] = { ...(pending[id] || {}), signature_html: html }
}

function discardChanges(id) {
  delete pending[id]
}

// The image to show: a pending upload (as a local object URL), nothing if the
// pending change is a removal, otherwise whatever the server has.
function previewUrl(mailbox, field) {
  const change = (pending[mailbox.id] || {})[field]
  if (change instanceof File) {
    const url = URL.createObjectURL(change)
    objectUrls.push(url)
    return url
  }
  if (change === '') return null
  return mailbox[field] || null
}

// --- server round trips -------------------------------------------------

function replaceMailbox(updated) {
  const idx = mailboxes.value.findIndex(m => m.id === updated.id)
  if (idx !== -1) mailboxes.value[idx] = updated
}

async function load() {
  loading.value = true
  try {
    const data = await getMailboxes(token())
    mailboxes.value = data.addresses || []
    users.value = data.candidate_users || []
    grantedToMe.value = data.granted_to_me || []
  } catch (e) {
    $toast.error('Failed to load mailboxes')
  } finally {
    loading.value = false
  }
}

async function saveBranding(mailbox) {
  const changes = pending[mailbox.id]
  if (!changes || !Object.keys(changes).length) return
  saving[mailbox.id] = true
  try {
    const payload = { ...changes }
    if (payload.signature_html !== undefined) {
      payload.signature_html = urlToCid(payload.signature_html, mailbox)
    }
    const updated = await updateMailboxBranding(token(), mailbox.id, payload)
    replaceMailbox(updated)
    delete pending[mailbox.id]
    $toast.success('Signature updated')
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not save signature')
  } finally {
    saving[mailbox.id] = false
  }
}

async function grant(mailbox) {
  const userId = selectedUser[mailbox.id]
  if (!userId) return
  try {
    const updated = await grantSender(token(), mailbox.id, userId)
    replaceMailbox(updated)
    selectedUser[mailbox.id] = null
    $toast.success('Access granted')
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not grant access')
  }
}

async function revoke(mailbox, userId) {
  try {
    const updated = await revokeSender(token(), mailbox.id, userId)
    replaceMailbox(updated)
    $toast.success('Access revoked')
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not revoke access')
  }
}

onMounted(load)
onBeforeUnmount(() => objectUrls.forEach(url => URL.revokeObjectURL(url)))
</script>
